/**
 * A casca do escritório — a mesma do Flux e do Amparo.
 *
 * Uma janela (`.tela`) sobre o papel de parede, com trilho de vidro flutuante
 * à esquerda (`.rail`) e, à direita, o painel opaco com a barra de ferramentas
 * unificada (`.topo`) grudada no topo. A geometria e as classes vêm da fonte
 * única da frota (`estilo/casca.css`); aqui só se monta o DOM e o conteúdo do
 * PCP GBU.
 *
 * ⚠️ A BARRA SEGUE O `CentriuHeader.tsx`: trilha da tela, cápsula de busca,
 * ações da página, sino de avisos e a conta à direita. O sino abre a LISTA de
 * avisos; o avatar abre o menu da conta, com o usuário de verdade do Supabase —
 * nada de nome fixo.
 */

import { formato, type Alerta } from '@pcpgbu/dominio';
import { h, render, sf, type Filho } from '@pcpgbu/ui';
import type { Aplicacao } from './estado.js';

export interface ItemDeNavegacao {
  rota: string;
  rotulo: string;
  icone: string;
  grupo: string;
  /** Contagem no canto direito da linha. Só aparece quando trava alguém. */
  badge?: number;
}

export const NAVEGACAO: ItemDeNavegacao[] = [
  { rota: 'carteira', rotulo: 'Carteira', icone: 'list-bullet', grupo: 'Produção' },
  { rota: 'quadro', rotulo: 'Quadro', icone: 'square-grid-2x2', grupo: 'Produção' },
  { rota: 'expedicao', rotulo: 'Expedição', icone: 'shippingbox', grupo: 'Produção' },
  { rota: 'painel', rotulo: 'Painel do dia', icone: 'waveform', grupo: 'Produção' },
  { rota: 'alertas', rotulo: 'Alertas', icone: 'bell', grupo: 'Produção' },
  { rota: 'importacoes', rotulo: 'Importações', icone: 'tray', grupo: 'Entrada de OS' },
  { rota: 'triagem', rotulo: 'Triagem', icone: 'questionmark-circle', grupo: 'Entrada de OS' },
  { rota: 'relatorios', rotulo: 'Relatórios', icone: 'sum', grupo: 'Análise' },
  { rota: 'fabrica', rotulo: 'Fábrica', icone: 'location', grupo: 'Cadastros' },
  { rota: 'pessoas', rotulo: 'Pessoas', icone: 'person-2', grupo: 'Cadastros' },
  { rota: 'aparelhos', rotulo: 'Aparelhos', icone: 'laptopcomputer', grupo: 'Cadastros' },
  { rota: 'parametros', rotulo: 'Parâmetros', icone: 'slider-horizontal-3', grupo: 'Cadastros' },
];

export interface Contagens {
  alertas: number;
  triagem: number;
  quarentena: number;
  atrasadas: number;
  tablets: number;
}

export interface Casca {
  elemento: HTMLElement;
  conteudo: HTMLElement;
  /** Redesenha trilho e trilha sem tocar no conteúdo. */
  atualizar(rota: string, titulo: string, contagens: Contagens, avisos: Alerta[]): void;
  definirAcoes(acoes: Filho): void;
}

export function montarCasca(app: Aplicacao): Casca {
  const conteudo = h('.conteudo');
  const menuLateral = h('nav.menu');
  const rodapeDoTrilho = h('.rail-pe');
  const areaAcoes = h('.row', { style: { gap: '8px', marginLeft: 'auto' } });
  const trilha = h('.trilha');

  // A busca do topo: a cápsula de vidro da casca da frota. O `input` vive fora
  // do `render` para não perder o foco a cada redesenho.
  const campo = h('input', {
    type: 'search',
    placeholder: 'Buscar OS, cliente ou produto',
    value: app.busca,
    oninput: (ev: Event) => {
      app.busca = (ev.target as HTMLInputElement).value;
      app.redesenhar();
    },
  }) as HTMLInputElement;
  const busca = h('.busca', { onclick: () => campo.focus() }, [sf('magnifyingglass'), campo]);

  // O sino abre a LISTA de avisos (o mesmo desenho do cabeçalho da frota).
  const painelAvisos = h('.menu-topo', { hidden: true });
  const sino = h('button.tbtn', {
    type: 'button',
    title: 'Avisos',
    'aria-label': 'Avisos',
    'aria-haspopup': 'menu',
    'aria-expanded': 'false',
    onclick: () => alternarAvisos(),
  });
  const sinoRel = h('.topo-rel', [sino, painelAvisos]);

  // A conta: o avatar abre o menu. Mesmo desenho do Amparo, com o usuário real.
  const painelConta = h('.menu-topo', { hidden: true });
  const botaoConta = h('button.eu-btn', {
    type: 'button',
    'aria-haspopup': 'menu',
    'aria-expanded': 'false',
    title: 'Sua conta',
    'aria-label': 'Menu da conta',
    onclick: () => alternarConta(),
  }, avatarDoUsuario(app));
  const contaRel = h('.topo-rel', [botaoConta, painelConta]);

  let avisosAbertos = false;
  let contaAberta = false;
  let ultimosAvisos: Alerta[] = [];
  const fecharTudo = (): void => {
    avisosAbertos = false;
    contaAberta = false;
    painelAvisos.hidden = true;
    painelConta.hidden = true;
    sino.setAttribute('aria-expanded', 'false');
    botaoConta.setAttribute('aria-expanded', 'false');
  };
  function alternarAvisos(): void {
    const abrir = !avisosAbertos;
    fecharTudo();
    avisosAbertos = abrir;
    if (abrir) {
      render(painelAvisos, conteudoDosAvisos(ultimosAvisos, app, fecharTudo));
      painelAvisos.hidden = false;
      sino.setAttribute('aria-expanded', 'true');
    }
  }
  function alternarConta(): void {
    const abrir = !contaAberta;
    fecharTudo();
    contaAberta = abrir;
    if (abrir) {
      render(painelConta, conteudoDaConta(app, fecharTudo));
      painelConta.hidden = false;
      botaoConta.setAttribute('aria-expanded', 'true');
    }
  }

  // Fecha ao clicar fora / no Escape.
  document.addEventListener('mousedown', (ev) => {
    const alvo = ev.target as Node;
    if (!sinoRel.contains(alvo) && !contaRel.contains(alvo)) fecharTudo();
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') fecharTudo();
  });

  const marca = h('.marca', [
    // O ícone de app do Tahoe: squircle no accent, glifo em branco.
    h('span.mac-app-icon.md', sf('list-bullet')),
    h('div', [
      h('p.marca-nome', 'PCP'),
      h('p.marca-sub', app.organizacaoNome ?? 'Controle de produção'),
    ]),
  ]);

  const trilho = h('aside.rail', [marca, menuLateral, rodapeDoTrilho]);

  const barra = h('header.topo', [trilha, busca, areaAcoes, sinoRel, contaRel]);

  const elemento = h('.tela', [trilho, h('.painel', [barra, conteudo])]);

  const atualizar = (
    rotaAtual: string,
    titulo: string,
    contagens: Contagens,
    avisos: Alerta[],
  ): void => {
    ultimosAvisos = avisos;
    const contagemDe = (rota: string): number | undefined => {
      if (rota === 'alertas') return contagens.alertas;
      if (rota === 'triagem') return contagens.triagem;
      if (rota === 'importacoes') return contagens.quarentena;
      if (rota === 'carteira') return contagens.atrasadas;
      return undefined;
    };

    const grupos = [...new Set(NAVEGACAO.map((i) => i.grupo))];
    render(
      menuLateral,
      grupos.map((grupo) =>
        h('div', [
          h('.grupo', grupo),
          ...NAVEGACAO.filter((i) => i.grupo === grupo).map((item) => {
            const n = contagemDe(item.rota) ?? 0;
            return h(
              `button.item${rotaAtual === item.rota ? '.ativo' : ''}`,
              { type: 'button', onclick: () => app.roteador.ir(`/${item.rota}`) },
              [
                sf(item.icone),
                h('span', item.rotulo),
                n > 0 ? h('span.tag', String(n)) : null,
              ],
            );
          }),
        ]),
      ),
    );

    // O bloco do produção, igual ao do Flux: quantos tablets existem.
    render(rodapeDoTrilho, [
      h('.producao-bloco', [
        h('p.producao-t', [sf('ipad'), 'Produção']),
        h(
          'p.producao-d',
          contagens.tablets === 0
            ? 'Nenhum tablet pareado ainda'
            : `${contagens.tablets} ${contagens.tablets === 1 ? 'tablet pareado' : 'tablets pareados'}`,
        ),
      ]),
    ]);

    render(sino, [
      sf('bell'),
      contagens.alertas > 0 ? h('i') : null,
    ]);

    // Se um menu estiver aberto, o conteúdo acompanha o novo estado.
    if (avisosAbertos) render(painelAvisos, conteudoDosAvisos(ultimosAvisos, app, fecharTudo));
    if (contaAberta) render(painelConta, conteudoDaConta(app, fecharTudo));

    const grupoAtual = NAVEGACAO.find((i) => i.rota === rotaAtual)?.grupo;
    render(trilha, [
      grupoAtual ? h('span', grupoAtual) : null,
      grupoAtual ? sf('chevron-right', 't-tertiary') : null,
      h('b', titulo),
    ]);
  };

  return {
    elemento,
    conteudo,
    atualizar,
    definirAcoes(acoes) {
      render(areaAcoes, acoes);
    },
  };
}

/**
 * A lista de avisos do sino — o mesmo desenho do cabeçalho da frota.
 *
 * Os avisos são os alertas abertos da produção (OS atrasada, etapa parada,
 * equipamento a classificar). Clicar leva à OS que gerou o aviso.
 */
function conteudoDosAvisos(avisos: Alerta[], app: Aplicacao, fechar: () => void): Filho {
  const lista: Filho[] = [h('.menu-topo-cab', [h('b', 'Avisos')]), h('.menu-topo-sep')];

  if (avisos.length === 0) {
    lista.push(h('.menu-topo-info', 'Nenhum aviso por aqui.'));
    return lista;
  }

  for (const a of avisos) {
    lista.push(
      h('button.menu-topo-item.menu-topo-aviso', {
        type: 'button',
        role: 'menuitem',
        onclick: () => {
          fechar();
          if (a.osNumero) app.roteador.ir(`/os/${a.osNumero}`);
          else app.roteador.ir('/alertas');
        },
      }, [
        h('span.ponto'),
        h('span.menu-topo-rt', [
          h('span.menu-topo-msg', a.texto),
          h('span.menu-topo-qd', formato.distanciaNoTempo(a.criadoEm)),
        ]),
      ]),
    );
  }
  return lista;
}

/**
 * O menu da conta — o mesmo do Amparo: quem está conectado em cima, os atalhos
 * de painel e configurações, e o sair. O nome e a foto vêm do Supabase.
 */
function conteudoDaConta(app: Aplicacao, fechar: () => void): Filho {
  const item = (rotulo: string, icone: string, aoClicar: () => void, classe = ''): HTMLElement =>
    h(`button.menu-topo-item${classe ? `.${classe}` : ''}`, {
      type: 'button',
      role: 'menuitem',
      onclick: () => {
        fechar();
        aoClicar();
      },
    }, [sf(icone), h('span', rotulo)]);

  return [
    h('.menu-topo-eu', [
      avatarDoUsuario(app, 'lg'),
      h('span.menu-topo-quem', [
        h('b', app.nuvem.nome ?? app.nuvem.email ?? 'Minha conta'),
        app.nuvem.email ? h('span', app.nuvem.email) : null,
      ]),
    ]),
    item('Painel do dia', 'waveform', () => app.roteador.ir('/painel')),
    item('Configurações', 'slider-horizontal-3', () => app.roteador.ir('/parametros')),
    h('.menu-topo-sep'),
    item('Sair', 'power', () => {
      void app.nuvem.sair().then(() => window.location.reload());
    }, 'saida'),
  ];
}

/** O avatar da conta: a foto, quando há; as iniciais do nome, quando não. */
function avatarDoUsuario(app: Aplicacao, tam: '' | 'lg' = ''): HTMLElement {
  const url = app.nuvem.avatarUrl;
  const nome = app.nuvem.nome ?? app.nuvem.email ?? '';
  const classe = `.eu-av${tam === 'lg' ? '.lg' : ''}`;
  if (url) {
    return h(`span${classe}`, h('img', { src: url, alt: '', referrerpolicy: 'no-referrer' }));
  }
  return h(`span${classe}`, iniciais(nome));
}

/** As duas primeiras iniciais do nome (ou do e-mail), para o avatar. */
function iniciais(nome: string): string {
  const local = nome.includes('@') ? (nome.split('@')[0] ?? '') : nome;
  const partes = local
    .replace(/[^\p{L}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const letras = partes.map((p) => p[0] ?? '').join('');
  return (letras || 'PC').slice(0, 2).toUpperCase();
}
